import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Save, Sparkles, Image as ImageIcon } from "lucide-react";
import { monthLabel, daysInMonth } from "@/hooks/useCalendar";

const MEAL_TYPES = ["breakfast", "lunch", "dinner", "snack"];
const TIERS = ["softening", "expanding", "awakening"];

// Utility: month options — offer Dec 2026 → Dec 2027 by default
function monthOptions() {
  const opts = [];
  for (let i = 0; i < 13; i++) {
    const d = new Date(2026, 11 + i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    opts.push({ key, label: monthLabel(key) });
  }
  return opts;
}

export default function CalendarAdmin() {
  const opts = monthOptions();
  const [month, setMonth] = useState("2026-12");
  const [monthDoc, setMonthDoc] = useState(null);
  const [dayIdx, setDayIdx] = useState(1);
  const [monthPayload, setMonthPayload] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const monthKey = month;
  const iso = `${monthKey}-${String(dayIdx).padStart(2, "0")}`;

  const loadAll = async () => {
    try {
      const [m, payload] = await Promise.all([
        api.get(`/admin/months/${monthKey}`),
        api.get(`/calendar/month/${monthKey}`),
      ]);
      setMonthDoc(m.data);
      setMonthPayload(payload.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { loadAll(); }, [monthKey]);

  const saveMonth = async (patch) => {
    const next = { ...monthDoc, ...patch };
    setMonthDoc(next);
    try {
      const { data } = await api.put(`/admin/months/${monthKey}`, {
        label: next.label,
        theme: next.theme,
        tier_notes: next.tier_notes,
      });
      setMonthDoc(data);
      toast.success("Month saved.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const seedDecember = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/seed-december");
      toast.success(`Seeded: ${data.recipes_added} recipes, ${data.movements_added} movements, ${data.coaching_added} coaching sessions.`);
      await loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSeeding(false); }
  };

  if (!monthDoc || !monthPayload) return <div className="label-eyebrow">Loading calendar…</div>;

  const dayRecipes = monthPayload.recipes.filter((r) => r.date === iso);
  const dayMovements = monthPayload.movements.filter((m) => m.date === iso);
  const dayCoaching = monthPayload.coaching.find((c) => c.date === iso);

  return (
    <div className="space-y-10" data-testid="calendar-admin">
      {/* Month selector + seed */}
      <section className="border-t border-b border-border grid md:grid-cols-12 gap-0">
        <div className="md:col-span-5 p-6 md:p-8 border-r border-border">
          <label className="label-eyebrow block mb-3">Month</label>
          <select
            value={monthKey}
            onChange={(e) => { setMonth(e.target.value); setDayIdx(1); }}
            data-testid="admin-month-select"
            className="w-full bg-transparent border-0 border-b border-border py-2 no-shadow-focus text-lg"
          >
            {opts.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
          <label className="label-eyebrow block mt-6 mb-2">Label</label>
          <input
            value={monthDoc.label}
            onChange={(e) => setMonthDoc({ ...monthDoc, label: e.target.value })}
            onBlur={(e) => saveMonth({ label: e.target.value })}
            data-testid="admin-month-label"
            className="w-full bg-transparent border-0 border-b border-border py-2 no-shadow-focus text-lg"
          />
          <label className="label-eyebrow block mt-6 mb-2">Theme</label>
          <input
            value={monthDoc.theme}
            onChange={(e) => setMonthDoc({ ...monthDoc, theme: e.target.value })}
            onBlur={(e) => saveMonth({ theme: e.target.value })}
            data-testid="admin-month-theme"
            placeholder="e.g. Root: Foundation & Reset"
            className="w-full bg-transparent border-0 border-b border-border py-2 no-shadow-focus text-lg"
          />
          <div className="mt-6 space-y-3">
            <div className="label-eyebrow">Tier notes (edit per month)</div>
            {TIERS.map((t) => (
              <textarea
                key={t}
                value={monthDoc.tier_notes?.[t] || ""}
                onChange={(e) => setMonthDoc({ ...monthDoc, tier_notes: { ...(monthDoc.tier_notes || {}), [t]: e.target.value } })}
                onBlur={() => saveMonth({ tier_notes: monthDoc.tier_notes })}
                data-testid={`admin-tier-note-${t}`}
                placeholder={`${t} tier note`}
                rows={2}
                className="w-full bg-transparent border border-border p-3 text-sm no-shadow-focus resize-none"
              />
            ))}
          </div>
        </div>
        <div className="md:col-span-7 p-6 md:p-8">
          <div className="label-eyebrow mb-3">Quick actions</div>
          <button
            onClick={seedDecember}
            disabled={seeding}
            data-testid="admin-seed-btn"
            className="inline-flex items-center gap-2 bg-[hsl(var(--foreground))] text-white px-5 py-3 text-sm hover:bg-[hsl(var(--primary))] transition-colors disabled:opacity-60"
          >
            <Sparkles className="w-4 h-4" strokeWidth={1.5} /> {seeding ? "Seeding…" : "Seed December 2026 from templates"}
          </button>
          <p className="text-xs text-stone-500 mt-3 leading-relaxed max-w-md">
            One-tap: fills Dec 1–28 with 112 recipes, 84 movements (3 tiers × 28 days), and 28 coaching sessions.
            Idempotent — safe to run more than once. You can edit or delete anything afterwards.
          </p>
        </div>
      </section>

      {/* Day picker */}
      <section>
        <div className="label-eyebrow mb-3">Pick a day in {monthDoc.label}</div>
        <div className="flex flex-wrap gap-2" data-testid="admin-day-picker">
          {Array.from({ length: daysInMonth(monthKey) }, (_, i) => {
            const dn = i + 1;
            const hasContent =
              monthPayload.recipes.some((r) => r.date === `${monthKey}-${String(dn).padStart(2, "0")}`) ||
              monthPayload.movements.some((m) => m.date === `${monthKey}-${String(dn).padStart(2, "0")}`) ||
              monthPayload.coaching.some((c) => c.date === `${monthKey}-${String(dn).padStart(2, "0")}`);
            return (
              <button
                key={dn}
                onClick={() => setDayIdx(dn)}
                data-testid={`admin-day-${dn}`}
                className={`px-3 py-2 text-xs border transition-colors ${dayIdx === dn ? "border-[hsl(var(--foreground))] bg-[hsl(var(--foreground))] text-[hsl(var(--background))]" : hasContent ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))]" : "border-border hover:border-stone-500"}`}
              >
                {String(dn).padStart(2, "0")}
              </button>
            );
          })}
        </div>
      </section>

      {/* Recipes CRUD */}
      <RecipesEditor
        date={iso}
        recipes={dayRecipes}
        onChange={loadAll}
      />

      {/* Movements CRUD */}
      <MovementsEditor
        date={iso}
        movements={dayMovements}
        onChange={loadAll}
      />

      {/* Coaching CRUD */}
      <CoachingEditor
        date={iso}
        coaching={dayCoaching}
        onChange={loadAll}
      />
    </div>
  );
}

/* ============= Photo upload helper ============= */
async function uploadPhoto(file, kind) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("kind", kind);
  const { data } = await api.post("/admin/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.path;
}

function PhotoField({ value, onChange, kind, testid }) {
  const [busy, setBusy] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem("rr_token");
  const url = value ? `${backend}/api/files/${value}?auth=${token}` : null;
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const path = await uploadPhoto(f, kind);
      onChange(path);
      toast.success("Photo uploaded.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-start gap-3">
      <label className="flex-1 border border-dashed border-border p-4 flex items-center justify-center cursor-pointer hover:border-[hsl(var(--primary))] transition-colors" data-testid={testid}>
        {url ? <img src={url} className="max-h-32 object-contain" alt="preview" /> : <span className="text-xs text-stone-500 inline-flex items-center gap-2"><Upload className="w-4 h-4" strokeWidth={1.5} /> {busy ? "Uploading…" : "Add a photo"}</span>}
        <input type="file" accept="image/*" onChange={onFile} className="hidden" />
      </label>
      {value && (
        <button onClick={() => onChange("")} className="text-xs text-stone-500 hover:text-[hsl(var(--destructive))]" data-testid={`${testid}-clear`}>Remove</button>
      )}
    </div>
  );
}

/* ============= Recipes ============= */
function RecipesEditor({ date, recipes, onChange }) {
  const [creating, setCreating] = useState(false);
  const emptyForm = () => ({
    date, meal_type: "breakfast", name: "", ingredients: [], directions: "",
    prep_min: 0, kcal: 0, photo_path: "",
  });
  const [form, setForm] = useState(emptyForm());
  useEffect(() => setForm(emptyForm()), [date]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Recipe needs a name.");
    setCreating(true);
    try {
      await api.post("/admin/recipes", form);
      setForm(emptyForm());
      toast.success("Recipe added.");
      await onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setCreating(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this recipe?")) return;
    await api.delete(`/admin/recipes/${id}`);
    await onChange();
    toast.success("Recipe removed.");
  };

  return (
    <section className="border-t border-border pt-8" data-testid="recipes-editor">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="label-eyebrow">Recipes · {date}</div>
          <h3 className="font-serif text-3xl mt-1">Meals for this day</h3>
        </div>
      </div>

      {recipes.length === 0 ? (
        <p className="text-stone-500 italic font-serif text-lg mb-8">No recipes yet.</p>
      ) : (
        <ul className="divide-y divide-border mb-10" data-testid="recipes-list">
          {recipes.map((r) => (
            <RecipeRow key={r.id} recipe={r} onSave={onChange} onDelete={() => remove(r.id)} />
          ))}
        </ul>
      )}

      {/* Create form */}
      <div className="border border-border p-6 grid md:grid-cols-12 gap-6" data-testid="recipe-form-create">
        <div className="md:col-span-5 space-y-4">
          <div>
            <label className="label-eyebrow block mb-1">Meal type</label>
            <select value={form.meal_type} onChange={(e) => setForm({ ...form, meal_type: e.target.value })} className="w-full bg-transparent border-0 border-b border-border py-2 no-shadow-focus" data-testid="recipe-meal-type">
              {MEAL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="recipe-name" className="w-full bg-transparent border-0 border-b border-border py-2 no-shadow-focus" placeholder="e.g. Overnight oats with berries" />
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="label-eyebrow block mb-1">Prep min</label>
              <input type="number" value={form.prep_min} onChange={(e) => setForm({ ...form, prep_min: parseInt(e.target.value || "0", 10) })} data-testid="recipe-prep" className="w-full bg-transparent border-0 border-b border-border py-2 no-shadow-focus" />
            </div>
            <div className="flex-1">
              <label className="label-eyebrow block mb-1">Kcal</label>
              <input type="number" value={form.kcal} onChange={(e) => setForm({ ...form, kcal: parseInt(e.target.value || "0", 10) })} data-testid="recipe-kcal" className="w-full bg-transparent border-0 border-b border-border py-2 no-shadow-focus" />
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Photo</label>
            <PhotoField value={form.photo_path} onChange={(p) => setForm({ ...form, photo_path: p })} kind="recipe" testid="recipe-photo-create" />
          </div>
        </div>
        <div className="md:col-span-7 space-y-4">
          <div>
            <label className="label-eyebrow block mb-1">Ingredients (one per line)</label>
            <textarea rows={5} value={form.ingredients.join("\n")} onChange={(e) => setForm({ ...form, ingredients: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} data-testid="recipe-ingredients" className="w-full bg-transparent border border-border p-3 no-shadow-focus resize-none text-sm" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Directions</label>
            <textarea rows={4} value={form.directions} onChange={(e) => setForm({ ...form, directions: e.target.value })} data-testid="recipe-directions" className="w-full bg-transparent border border-border p-3 no-shadow-focus resize-none text-sm" />
          </div>
          <button onClick={save} disabled={creating} data-testid="recipe-save" className="inline-flex items-center gap-2 bg-[hsl(var(--primary))] text-white px-6 py-3 hover:bg-[hsl(var(--foreground))] transition-colors disabled:opacity-60">
            <Plus className="w-4 h-4" strokeWidth={1.5} /> {creating ? "Saving…" : "Add recipe"}
          </button>
        </div>
      </div>
    </section>
  );
}

function RecipeRow({ recipe, onSave, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState(recipe);
  useEffect(() => setDraft(recipe), [recipe.id]);
  const backend = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem("rr_token");
  const url = draft.photo_path ? `${backend}/api/files/${draft.photo_path}?auth=${token}` : null;

  const save = async () => {
    try {
      await api.put(`/admin/recipes/${recipe.id}`, draft);
      toast.success("Recipe saved.");
      setExpanded(false);
      await onSave();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <li className="py-4" data-testid={`recipe-row-${recipe.id}`}>
      <div className="flex items-baseline justify-between gap-4">
        <div className="flex items-center gap-3">
          {url && <img src={url} className="w-10 h-10 object-cover" alt="" />}
          <div>
            <div className="label-eyebrow">{recipe.meal_type}</div>
            <div className="font-serif text-lg">{recipe.name}</div>
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setExpanded((s) => !s)} data-testid={`recipe-edit-${recipe.id}`} className="text-xs text-stone-500 hover:text-[hsl(var(--foreground))]">{expanded ? "Close" : "Edit"}</button>
          <button onClick={onDelete} data-testid={`recipe-del-${recipe.id}`} className="text-stone-400 hover:text-[hsl(var(--destructive))]"><Trash2 className="w-4 h-4" strokeWidth={1.25} /></button>
        </div>
      </div>
      {expanded && (
        <div className="mt-4 grid md:grid-cols-12 gap-4 border-l border-border pl-4">
          <div className="md:col-span-5 space-y-3">
            <select value={draft.meal_type} onChange={(e) => setDraft({ ...draft, meal_type: e.target.value })} className="w-full bg-transparent border-0 border-b border-border py-2 no-shadow-focus">
              {MEAL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="w-full bg-transparent border-0 border-b border-border py-2 no-shadow-focus" />
            <div className="flex gap-3">
              <input type="number" value={draft.prep_min} onChange={(e) => setDraft({ ...draft, prep_min: parseInt(e.target.value || "0", 10) })} className="flex-1 bg-transparent border-0 border-b border-border py-2 no-shadow-focus" />
              <input type="number" value={draft.kcal} onChange={(e) => setDraft({ ...draft, kcal: parseInt(e.target.value || "0", 10) })} className="flex-1 bg-transparent border-0 border-b border-border py-2 no-shadow-focus" />
            </div>
            <PhotoField value={draft.photo_path} onChange={(p) => setDraft({ ...draft, photo_path: p })} kind="recipe" testid={`recipe-photo-${recipe.id}`} />
          </div>
          <div className="md:col-span-7 space-y-3">
            <textarea rows={4} value={draft.ingredients.join("\n")} onChange={(e) => setDraft({ ...draft, ingredients: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} className="w-full bg-transparent border border-border p-3 no-shadow-focus resize-none text-sm" />
            <textarea rows={4} value={draft.directions} onChange={(e) => setDraft({ ...draft, directions: e.target.value })} className="w-full bg-transparent border border-border p-3 no-shadow-focus resize-none text-sm" />
            <button onClick={save} data-testid={`recipe-save-${recipe.id}`} className="inline-flex items-center gap-2 border border-[hsl(var(--foreground))] px-5 py-2 text-sm hover:bg-[hsl(var(--foreground))] hover:text-white transition-colors">
              <Save className="w-4 h-4" strokeWidth={1.5} /> Save
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

/* ============= Movements ============= */
function MovementsEditor({ date, movements, onChange }) {
  const emptyForm = () => ({
    date, tier: "expanding", title: "", focus: "", duration_min: 30, note: "",
    photo_path: "", moves: [{ name: "", sets: 3, reps: "10", form_cue: "" }],
  });
  const [form, setForm] = useState(emptyForm());
  useEffect(() => setForm(emptyForm()), [date]);
  const [creating, setCreating] = useState(false);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Movement needs a title.");
    setCreating(true);
    try {
      await api.post("/admin/movements", form);
      setForm(emptyForm());
      toast.success("Movement added.");
      await onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setCreating(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this movement?")) return;
    await api.delete(`/admin/movements/${id}`);
    await onChange();
    toast.success("Movement removed.");
  };

  return (
    <section className="border-t border-border pt-8" data-testid="movements-editor">
      <div className="mb-6">
        <div className="label-eyebrow">Movements · {date}</div>
        <h3 className="font-serif text-3xl mt-1">Movement plan for this day</h3>
        <p className="text-xs text-stone-500 mt-2">Create one entry per tier (Softening / Expanding / Awakening).</p>
      </div>

      {movements.length === 0 ? (
        <p className="text-stone-500 italic font-serif text-lg mb-8">No movement scheduled yet.</p>
      ) : (
        <ul className="divide-y divide-border mb-10" data-testid="movements-list">
          {movements.map((m) => (
            <MovementRow key={m.id} movement={m} onSave={onChange} onDelete={() => remove(m.id)} />
          ))}
        </ul>
      )}

      <div className="border border-border p-6 space-y-4" data-testid="movement-form-create">
        <div className="grid md:grid-cols-12 gap-4">
          <div className="md:col-span-3">
            <label className="label-eyebrow block mb-1">Tier</label>
            <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} className="w-full bg-transparent border-0 border-b border-border py-2 no-shadow-focus" data-testid="movement-tier">
              {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="md:col-span-5">
            <label className="label-eyebrow block mb-1">Title</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="movement-title" className="w-full bg-transparent border-0 border-b border-border py-2 no-shadow-focus" placeholder="e.g. Full Body Awakening" />
          </div>
          <div className="md:col-span-2">
            <label className="label-eyebrow block mb-1">Focus</label>
            <input value={form.focus} onChange={(e) => setForm({ ...form, focus: e.target.value })} data-testid="movement-focus" className="w-full bg-transparent border-0 border-b border-border py-2 no-shadow-focus" placeholder="Cardio" />
          </div>
          <div className="md:col-span-2">
            <label className="label-eyebrow block mb-1">Duration (min)</label>
            <input type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: parseInt(e.target.value || "0", 10) })} data-testid="movement-duration" className="w-full bg-transparent border-0 border-b border-border py-2 no-shadow-focus" />
          </div>
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Note</label>
          <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="movement-note" className="w-full bg-transparent border-0 border-b border-border py-2 no-shadow-focus" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Photo</label>
          <PhotoField value={form.photo_path} onChange={(p) => setForm({ ...form, photo_path: p })} kind="movement" testid="movement-photo-create" />
        </div>
        <MovesEditor moves={form.moves} onChange={(moves) => setForm({ ...form, moves })} />
        <button onClick={save} disabled={creating} data-testid="movement-save" className="inline-flex items-center gap-2 bg-[hsl(var(--primary))] text-white px-6 py-3 hover:bg-[hsl(var(--foreground))] transition-colors disabled:opacity-60">
          <Plus className="w-4 h-4" strokeWidth={1.5} /> {creating ? "Saving…" : "Add movement"}
        </button>
      </div>
    </section>
  );
}

function MovementRow({ movement, onSave, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState(movement);
  useEffect(() => setDraft(movement), [movement.id]);
  const backend = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem("rr_token");
  const url = draft.photo_path ? `${backend}/api/files/${draft.photo_path}?auth=${token}` : null;

  const save = async () => {
    try {
      await api.put(`/admin/movements/${movement.id}`, draft);
      toast.success("Movement saved.");
      setExpanded(false);
      await onSave();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <li className="py-4" data-testid={`movement-row-${movement.id}`}>
      <div className="flex items-baseline justify-between gap-4">
        <div className="flex items-center gap-3">
          {url && <img src={url} className="w-10 h-10 object-cover" alt="" />}
          <div>
            <div className="label-eyebrow">{movement.tier} · {movement.focus} · {movement.duration_min} min</div>
            <div className="font-serif text-lg">{movement.title}</div>
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setExpanded((s) => !s)} data-testid={`movement-edit-${movement.id}`} className="text-xs text-stone-500 hover:text-[hsl(var(--foreground))]">{expanded ? "Close" : "Edit"}</button>
          <button onClick={onDelete} data-testid={`movement-del-${movement.id}`} className="text-stone-400 hover:text-[hsl(var(--destructive))]"><Trash2 className="w-4 h-4" strokeWidth={1.25} /></button>
        </div>
      </div>
      {expanded && (
        <div className="mt-4 space-y-3 border-l border-border pl-4">
          <div className="grid md:grid-cols-12 gap-3">
            <select value={draft.tier} onChange={(e) => setDraft({ ...draft, tier: e.target.value })} className="md:col-span-3 bg-transparent border-0 border-b border-border py-2 no-shadow-focus">
              {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="md:col-span-5 bg-transparent border-0 border-b border-border py-2 no-shadow-focus" />
            <input value={draft.focus} onChange={(e) => setDraft({ ...draft, focus: e.target.value })} className="md:col-span-2 bg-transparent border-0 border-b border-border py-2 no-shadow-focus" />
            <input type="number" value={draft.duration_min} onChange={(e) => setDraft({ ...draft, duration_min: parseInt(e.target.value || "0", 10) })} className="md:col-span-2 bg-transparent border-0 border-b border-border py-2 no-shadow-focus" />
          </div>
          <input value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} className="w-full bg-transparent border-0 border-b border-border py-2 no-shadow-focus" placeholder="Note" />
          <PhotoField value={draft.photo_path} onChange={(p) => setDraft({ ...draft, photo_path: p })} kind="movement" testid={`movement-photo-${movement.id}`} />
          <MovesEditor moves={draft.moves} onChange={(moves) => setDraft({ ...draft, moves })} />
          <button onClick={save} data-testid={`movement-save-${movement.id}`} className="inline-flex items-center gap-2 border border-[hsl(var(--foreground))] px-5 py-2 text-sm hover:bg-[hsl(var(--foreground))] hover:text-white transition-colors">
            <Save className="w-4 h-4" strokeWidth={1.5} /> Save
          </button>
        </div>
      )}
    </li>
  );
}

function MovesEditor({ moves, onChange }) {
  const update = (i, patch) => onChange(moves.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  const remove = (i) => onChange(moves.filter((_, idx) => idx !== i));
  const add = () => onChange([...moves, { name: "", sets: 3, reps: "10", form_cue: "" }]);
  return (
    <div>
      <div className="label-eyebrow mb-2">Moves</div>
      <ul className="space-y-2">
        {moves.map((m, i) => (
          <li key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`move-${i}`}>
            <input value={m.name} onChange={(e) => update(i, { name: e.target.value })} className="col-span-4 bg-transparent border-0 border-b border-border py-1.5 no-shadow-focus text-sm" placeholder="Move name" />
            <input type="number" value={m.sets} onChange={(e) => update(i, { sets: parseInt(e.target.value || "0", 10) })} className="col-span-1 bg-transparent border-0 border-b border-border py-1.5 no-shadow-focus text-sm" />
            <input value={m.reps} onChange={(e) => update(i, { reps: e.target.value })} className="col-span-2 bg-transparent border-0 border-b border-border py-1.5 no-shadow-focus text-sm" placeholder="Reps" />
            <input value={m.form_cue} onChange={(e) => update(i, { form_cue: e.target.value })} className="col-span-4 bg-transparent border-0 border-b border-border py-1.5 no-shadow-focus text-sm" placeholder="Form cue" />
            <button onClick={() => remove(i)} className="col-span-1 text-stone-400 hover:text-[hsl(var(--destructive))]" data-testid={`move-del-${i}`}><Trash2 className="w-3.5 h-3.5" strokeWidth={1.25} /></button>
          </li>
        ))}
      </ul>
      <button onClick={add} data-testid="move-add" className="mt-3 text-xs text-[hsl(var(--primary))] hover:underline inline-flex items-center gap-1">
        <Plus className="w-3 h-3" strokeWidth={1.5} /> Add move
      </button>
    </div>
  );
}

/* ============= Coaching ============= */
function CoachingEditor({ date, coaching, onChange }) {
  const emptyForm = () => ({
    date, title: "", affirmation: "", reflection: "", action: "",
  });
  const [form, setForm] = useState(coaching || emptyForm());
  useEffect(() => setForm(coaching || emptyForm()), [date, coaching?.id]);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Coaching needs a title.");
    setSaving(true);
    try {
      await api.put("/admin/coaching", form);
      toast.success("Coaching saved.");
      await onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!coaching?.id) return;
    if (!window.confirm("Delete this coaching session?")) return;
    await api.delete(`/admin/coaching/${coaching.id}`);
    setForm(emptyForm());
    await onChange();
  };

  return (
    <section className="border-t border-border pt-8" data-testid="coaching-editor">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="label-eyebrow">Coaching · {date}</div>
          <h3 className="font-serif text-3xl mt-1">One session for this day</h3>
        </div>
        {coaching?.id && <button onClick={remove} className="text-xs text-stone-500 hover:text-[hsl(var(--destructive))]" data-testid="coaching-delete">Delete</button>}
      </div>

      <div className="border border-border p-6 space-y-4">
        <div>
          <label className="label-eyebrow block mb-1">Title</label>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="coaching-title" className="w-full bg-transparent border-0 border-b border-border py-2 no-shadow-focus text-lg" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Affirmation</label>
          <input value={form.affirmation} onChange={(e) => setForm({ ...form, affirmation: e.target.value })} data-testid="coaching-affirmation" className="w-full bg-transparent border-0 border-b border-border py-2 no-shadow-focus" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Reflection</label>
          <textarea rows={3} value={form.reflection} onChange={(e) => setForm({ ...form, reflection: e.target.value })} data-testid="coaching-reflection" className="w-full bg-transparent border border-border p-3 no-shadow-focus resize-none text-sm" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Action</label>
          <textarea rows={2} value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })} data-testid="coaching-action" className="w-full bg-transparent border border-border p-3 no-shadow-focus resize-none text-sm" />
        </div>
        <button onClick={save} disabled={saving} data-testid="coaching-save" className="inline-flex items-center gap-2 bg-[hsl(var(--primary))] text-white px-6 py-3 hover:bg-[hsl(var(--foreground))] transition-colors disabled:opacity-60">
          <Save className="w-4 h-4" strokeWidth={1.5} /> {saving ? "Saving…" : "Save coaching"}
        </button>
      </div>
    </section>
  );
}
